import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { ChapterButtonsLocation } from 'GeminiViewerComponent/helpers';
import { getPdfjsViewerUrl } from 'GeminiViewerComponent/helpers/urlUtils.js';
import { isUrlLearningContent } from 'helpers/learningItemUtils';
import { activateLoading, deactivateLoading } from 'GeminiViewerComponent/features/globals/loadingProgressSlice';
import { fetchContentById, fetchContentVersions } from 'features/content/contentSlice';
import SelectContentForm from 'components/ProcedureDesigner/components/ContentNodeEditor/SelectContentForm';
import { selectActiveTheme } from 'GeminiViewerComponent/features/globals/themeSlice';
import { SnackbarDismiss } from 'GeminiViewerComponent/components/SnackbarDismiss';

export function registerPlugin(tinymce) {
    tinymce.PluginManager.add('addContent', function (editor) {
        editor.ui.registry.addIcon(
            'contentLinkIcon',
            '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2m0 16H5V5h14zm-5.06-8.94c.63.64.98 1.48.98 2.38s-.35 1.74-.98 2.37L11.73 17c-.65.67-1.51 1-2.37 1S7.64 17.67 7 17c-1.33-1.29-1.33-3.42 0-4.74l1.35-1.36l-.01.6c-.01.5.07 1 .23 1.44l.05.15l-.4.41a1.597 1.597 0 0 0 0 2.28c.61.62 1.67.62 2.28 0l2.2-2.19c.3-.31.48-.72.48-1.15c0-.44-.18-.83-.48-1.14a.87.87 0 0 1 0-1.24c.33-.33.91-.32 1.24 0m4.06-.7c0 .9-.35 1.74-1 2.38l-1.34 1.36v-.6c.01-.5-.07-1-.23-1.44l-.05-.14l.4-.42a1.597 1.597 0 0 0 0-2.28c-.61-.62-1.68-.61-2.28 0l-2.2 2.2c-.3.3-.48.71-.48 1.14c0 .44.18.83.48 1.14c.17.16.26.38.26.62s-.09.46-.26.62a.86.86 0 0 1-.62.25c-.22 0-.45-.08-.62-.25a3.36 3.36 0 0 1 0-4.75L12.27 7A3.31 3.31 0 0 1 17 7c.65.62 1 1.46 1 2.36"/></svg>'
        );
        editor.ui.registry.addButton('addContentLink', {
            icon: 'contentLinkIcon',
            tooltip: 'Add link to existing content',
            onAction: () => {
                editor.fire('addContent', { contentType: 'link' });
                editor.fire('AfterProgressState', { state: true }); // hack to hide context menu
            },
        });

        editor.ui.registry.addIcon(
            'inlineImageIcon',
            '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M19 19H5V5h14m0-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2m-5.04 9.29l-2.75 3.54l-1.96-2.36L6.5 17h11z"/></svg>'
        );
        editor.ui.registry.addButton('addInlineImage', {
            icon: 'inlineImageIcon',
            tooltip: 'Add inline image',
            onAction: () => {
                editor.fire('addContent', { contentType: 'image' });
                editor.fire('AfterProgressState', { state: true });
            },
        });

        editor.ui.registry.addIcon(
            'inlineVideoIcon',
            '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2C6.48,2 2,6.48 2,12C2,17.52 6.48,22 12,22C17.52,22 22,17.52 22,12C22,6.48 17.52,2 12,2M10,16.5L16,12L10,7.5V16.5Z" /></svg>'
        );
        editor.ui.registry.addButton('addInlineVideo', {
            icon: 'inlineVideoIcon',
            tooltip: 'Add inline video',
            onAction: () => {
                editor.fire('addContent', { contentType: 'video' });
                editor.fire('AfterProgressState', { state: true });
            },
        });


        editor.ui.registry.addIcon(
            'inlinePDFIcon',
            '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m-9.5 8.5c0 .8-.7 1.5-1.5 1.5H7v2H5.5V9H8c.8 0 1.5.7 1.5 1.5zm5 2c0 .8-.7 1.5-1.5 1.5h-2.5V9H13c.8 0 1.5.7 1.5 1.5zm4-3H17v1h1.5V13H17v2h-1.5V9h3zm-6.5 0h1v3h-1zm-5 0h1v1H7z"/></svg>'
        );
        editor.ui.registry.addButton('addInlinePdf', {
            icon: 'inlinePDFIcon',
            tooltip: 'Add inline PDF',
            onAction: () => {
                editor.fire('addContent', { contentType: 'pdf' });
                editor.fire('AfterProgressState', { state: true });
            },
        });

        editor.ui.registry.addIcon(
            'inlineAudioIcon',
            '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M12 3v18c4.97 0 9-4.03 9-9s-4.03-9-9-9m0 3a6 6 0 0 1 0 12V6M3 9v6h4l5 5V4L7 9H3z"/></svg>'
        );
        editor.ui.registry.addButton('addInlineAudio', {
            icon: 'inlineAudioIcon',
            tooltip: 'Add inline Audio',
            onAction: () => {
                editor.fire('addContent', { contentType: 'audio' });
                editor.fire('AfterProgressState', { state: true });
            },
        });

        editor.ui.registry.addIcon(
            'inlineLearningItemIcon',
            '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z"/></svg>'
        );
        editor.ui.registry.addButton('addInlineLearningItem', {
            icon: 'inlineLearningItemIcon',
            tooltip: 'Add inline Learning Item / Activity',
            onAction: () => {
                editor.fire('addContent', { contentType: 'learning' });
                editor.fire('AfterProgressState', { state: true });
            },
        });

    });
}

function getLinkHtml(contentData, content, contentName) {
    let html = contentData.is_button ? '<button data-target-content="true">' : '';
    html += '<a data-target-content="true" ';
    if (contentData.chapter_id) {
        html += `data-chapter-id=${contentData.chapter_id} `;
    }
    if (contentData?.selectedVersion) {
        html += `data-target-selectedVersion="${contentData?.selectedVersion}" `;
    }
    if (contentData.show_chapters !== undefined) {
        html += `data-target-show_chapters="${contentData.show_chapters}" `;
    }
    if (contentData.collapse_chapters !== undefined) {
        html += `data-target-collapse_chapters="${contentData.collapse_chapters}" `;
    }
    if (contentData.chapter_buttons_location) {
        html += `data-target-chapter_buttons_location="${contentData.chapter_buttons_location}" `;
    }
    if (contentData.selectedChapter) {
        html += `data-target-selectedChapter="${contentData.selectedChapter}" `;
    }
    html += `data-target-contentid="${contentData.content_id}" data-target-refmode="${
        contentData.reference_mode
    }" data-target-auto-play="${contentData.auto_play}" data-target-link-type-id="${
        content.default_link_type_id || contentName?.payload?.default_link_type_id
    }" href="`;
    if (content?.encoding_status?.toLowerCase() === 'complete') {
        html += `${content?.encoded_url}?`;
    } else if (content?.encoded_url) {
        html += `${content.encoded_url}?`;
    } else {
        html += `${content?.url}?`;
    }
    if (contentData.auto_play) {
        html += `auto_play=${contentData.auto_play}&`;
    }
    html += `link_type_id=${content.default_link_type_id || contentName?.payload?.default_link_type_id}`;

    html += `" target="${
        contentData.display_mode === 'external'
            ? '_blank'
            : contentData.display_mode === 'inline'
            ? '_self'
            : contentData.display_mode
    }">${contentData.link_text}</a>`;

    if (contentData.is_button) {
        html += '</button>';
    }
    return html;
}

function getVideoHtml(contentData, content) {
    // 2025.11.25 HW: change wrapping div style to be more responsive
    let html = `<div class="media-wrapper" contenteditable="false" style="width: 100%; max-width:${contentData.max_width || 1600}px; margin:0 auto;" data-target-content="true" data-target-contentid="${contentData.content_id}" data-target-show_chapters="${contentData.show_chapters}" data-target-collapse_chapters="${contentData.collapse_chapters}" data-target-chapter_buttons_location="${contentData.chapter_buttons_location}" `;
    if (contentData.auto_play) {
        html += `data-target-auto_play="${contentData.auto_play}" `;
    }
    if (contentData.poster_image_name) {
        html += `data-target-poster_image_name="${contentData.poster_image_name}" `;
    }
    if (contentData.poster_image_url) {
        html += `data-target-poster_image_url="${contentData.poster_image_url}" `;
        html += `data-target-poster_id="${contentData.poster_id}" `;
        if (contentData.posterVersion) {
            html += `data-target-posterVersion="${contentData.posterVersion}" `;
        }
    }
    if (contentData.selectedVersion) {
        html += `data-target-selectedVersion="${contentData.selectedVersion}" `;
    }
    if (contentData.selectedChapter) {
        html += `data-target-selectedChapter="${contentData.selectedChapter}" `;
    }
    html += `>`;
    html += `<video controls draggable="false" style="width:100%; height:auto; display:block;" data-target-contentid="${contentData.content_id}" data-target-show_chapters="${
        contentData.show_chapters
    }" data-target-collapse_chapters="${contentData.collapse_chapters}" data-target-chapter_buttons_location="${
        contentData.chapter_buttons_location
    }"`; // height="${contentData.height || 225}" width="${contentData.width || 400}" `;

    if (contentData.auto_play) {
        html += `autoplay `;
    }
    if (contentData.selectedVersion) {
        html += `data-target-selectedVersion="${contentData.selectedVersion}" `;
    }
    if (contentData?.selectedChapter) {
        html += `data-target-selectedChapter="${contentData.selectedChapter}" `;
    }
    if (contentData.poster_image_url) {
        html += `poster="${contentData.poster_image_url}" `;
    }
    html += `src="`;
    if (content.encoding_status?.toLowerCase() === 'complete') {
        html += `${content.encoded_url}`;
    } else if (content?.encoded_url) {
        html += `${content.encoded_url}`;
    } else {
        html += `${content.url}`; // 2025.11.25 HW: I removed the trailing ? as it causes the video not to load in Chrome
    }
    html += `" >`;
    html += `</video></div>`;
    return html;
}

function getImageHtml(contentData, content) {
    let html = `<img data-target-content="true" data-target-contentid="${contentData.content_id}" src="${content.url}" `;
    if (contentData.selectedVersion) {
        html += `data-target-selectedVersion="${contentData.selectedVersion}" `;
    }

    let styleParts = [];

    // Width logic
    if (contentData.width) {
        styleParts.push(`width: ${contentData.width}px`);
    } else {
        styleParts.push(`width: 100%`);
        styleParts.push(`max-width: ${contentData.max_width || 1600}px`);
    }

    // Height logic
    if (contentData.height) {
        styleParts.push(`height: ${contentData.height}px`);
    }

    // Centering
    styleParts.push(`margin: 0 auto`);

    const style = styleParts.join('; ') + ';';

    if (style) {
        html += `style="${style}" `;
    }

    if (contentData.altText) {
        html += `alt="${contentData.altText}" `;
    }
    html += '/>';

    return html;
}

function addDivWrapper(contentData, htmlElement){
    let html_style = `style="width: 100%; max-width:${contentData.max_width || 1600}px; margin:0 auto;"`;

    let html = `<div class="media-wrapper" ${html_style} data-target-content="true" data-target-contentid="${contentData.content_id}" `;
    
    if (contentData.selectedVersion) {
        html += `data-target-selectedVersion="${contentData.selectedVersion}" `;
    }
    html += '>';
    html += htmlElement;
    html += '</div>';
    return html;
}

function getPdfHtml(contentData, content, activeTheme) {
    const wrapper = document.createElement("div");

    // Identify as a PDF block
    wrapper.className = "gem-pdf pdf-block";
    wrapper.setAttribute("contenteditable", "false");

    // Keep your existing metadata (used by your app)
    wrapper.dataset.targetContent = "true";
    wrapper.dataset.targetContentid = String(contentData.content_id ?? "");
    if (contentData.selectedVersion) {
        wrapper.dataset.targetSelectedversion = String(contentData.selectedVersion);
    }

    // Source URL (keep raw here; we'll encode for iframe src)
    const pdfUrl = content.url;
    wrapper.dataset.src = pdfUrl;

    // Options
    const page = Number(contentData.pageNumber || 1);
    wrapper.dataset.page = String(page);

    if (contentData.hideToolbar) wrapper.dataset.toolbar = "0";

    const zoom = contentData.zoom || 'page-fit';
    wrapper.dataset.zoom = String(zoom);

    const pageMode = contentData.pagemode || "none"; // default pageMode to 'None', collapsing the sidebar
    wrapper.dataset.pagemode = pageMode;

    const fullscreenMode = contentData.fullscreenmode || false; // default fullscreenMode to 'false' i.e. not full screen
    wrapper.dataset.fullscreenmode = fullscreenMode;

    const btnFullscreenOpacity = Number(contentData.btn_fullscreen_opacity || 80); // default btnFullscreenOpacity to 80 
    wrapper.dataset.btn_fullscreen_opacity = String(btnFullscreenOpacity);

    const btnFullscreenSize = contentData.btn_fullscreen_size || 'normal'; // default btnFullscreenSize to normal
    wrapper.dataset.btn_fullscreen_size = String(btnFullscreenSize);

    if (contentData.title) {
        wrapper.dataset.title = String(contentData.title);
        wrapper.title = String(contentData.title);
    }

    const aspect_ratio_width = `${contentData.width || 8.5}`;
    const aspect_ratio_height = `${contentData.height || 11}`;
    wrapper.dataset.aspect_ratio_width = aspect_ratio_width;
    wrapper.dataset.aspect_ratio_height = aspect_ratio_height;

    // Layout (note: overflow hidden is usually better for iframes)
    //wrapper.style.width = contentData.width ? `${contentData.width}px` : `100%`;
    wrapper.style.width = '100%'; // this is necessary for aspect-ratio
    //wrapper.style.height = contentData.height ? `${contentData.height}px` : `clamp(320px, 70vh, 1000px)`
    wrapper.style.maxWidth = `${contentData.max_width || 1600}px`;
    wrapper.style.aspectRatio = `${aspect_ratio_width} / ${aspect_ratio_height}`;
    wrapper.style.border = "1px solid #ccc";
    wrapper.style.borderRadius = "8px";
    wrapper.style.background = "#f7f7f7";
    wrapper.style.overflow = "hidden";


    const hidebuttons =
        "editorcomment,editorsignature,editorhighlight,editorfreetext,editorink,editorstamp,print,download,secondarytoolbartoggle";

    const iframeSrc = getPdfjsViewerUrl(
        pdfUrl,
        Boolean(contentData.hideToolbar),
        String(page),
        zoom,
        pageMode,
        hidebuttons,
        fullscreenMode,
        btnFullscreenOpacity,
        btnFullscreenSize
    );

    // Embed the iframe inside the non-editable wrapper
    wrapper.innerHTML = `
        <iframe
        class="pdf-iframe"
        title="${escapeHtml(contentData.title || "PDF")}"
        src="${escapeHtml(iframeSrc)}"
        style="width:100%;height:100%;border:0;display:block;"
        ></iframe>
    `;
    return wrapper.outerHTML;
}


// Helper to avoid injecting HTML in title
function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, (c) => ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
    }[c]));
}


function getLearningItemHtml(contentData, content) {
    // Authors never see or edit iframe HTML directly — the wrapper encapsulates the launch detail.
    const launchUrl = content.url; // already has launch_file appended by prefixUrl in content.service.js
    const maxWidth = contentData.max_width || 1600;

    // video wrapper convention.
    let wrapperStyle;
    if (contentData.width && contentData.height) {
        wrapperStyle = `width:${contentData.width}px; height:${contentData.height}px; max-width:${maxWidth}px; margin:0 auto;`;
    } else if (contentData.width) {
        wrapperStyle = `width:${contentData.width}px; max-width:${maxWidth}px; margin:0 auto; aspect-ratio: 16 / 9;`;
    } else {
        wrapperStyle = `width: 100%; max-width:${maxWidth}px; margin:0 auto; aspect-ratio: 16 / 9;`;
    }

    let html = `<div class="media-wrapper gem-learning-item" contenteditable="false" `;
    html += `style="${wrapperStyle}" `;
    html += `data-target-content="true" data-target-contentid="${contentData.content_id}" `;
    if (contentData.width) {
        html += `data-target-width="${contentData.width}" `;
    }
    if (contentData.height) {
        html += `data-target-height="${contentData.height}" `;
    }
    if (contentData.selectedVersion) {
        html += `data-target-selectedVersion="${contentData.selectedVersion}" `;
    }
    html += `>`;
    html += `<iframe `;
    html += `src="${escapeHtml(launchUrl)}" `;
    html += `style="width:100%;height:100%;border:0;display:block;" `;
    html += `allowfullscreen `;
    html += `></iframe>`;
    html += `</div>`;
    return html;
}

function getLearningItemHtmlForUrl(contentData, content) {
    // Identical to getLearningItemHtml() with two differences:
    // 1. data-target-learning-display-mode="inline" added to wrapper <div>
    // 2. sandbox attribute added to <iframe>
    const launchUrl = content.url; // already has launch_file appended by prefixUrl in content.service.js
    const maxWidth = contentData.max_width || 1600;

    // video wrapper convention.
    let wrapperStyle;
    if (contentData.width && contentData.height) {
        wrapperStyle = `width:${contentData.width}px; height:${contentData.height}px; max-width:${maxWidth}px; margin:0 auto;`;
    } else if (contentData.width) {
        wrapperStyle = `width:${contentData.width}px; max-width:${maxWidth}px; margin:0 auto; aspect-ratio: 16 / 9;`;
    } else {
        wrapperStyle = `width: 100%; max-width:${maxWidth}px; margin:0 auto; aspect-ratio: 16 / 9;`;
    }

    let html = `<div class="media-wrapper gem-learning-item" contenteditable="false" `;
    html += `style="${wrapperStyle}" `;
    html += `data-target-content="true" data-target-contentid="${contentData.content_id}" `;
    html += `data-target-learning-display-mode="inline" `;
    if (contentData.width) {
        html += `data-target-width="${contentData.width}" `;
    }
    if (contentData.height) {
        html += `data-target-height="${contentData.height}" `;
    }
    if (contentData.selectedVersion) {
        html += `data-target-selectedVersion="${contentData.selectedVersion}" `;
    }
    html += `>`;
    html += `<iframe `;
    html += `src="${escapeHtml(launchUrl)}" `;
    html += `style="width:100%;height:100%;border:0;display:block;" `;
    html += `sandbox="allow-scripts allow-same-origin allow-forms allow-popups" `;
    html += `allowfullscreen `;
    html += `></iframe>`;
    html += `</div>`;
    return html;
}

function getAudioHtml(contentData, content) {
    let html = `<audio controls src="${content.url}" data-target-contentid="${contentData.content_id}" `;
    if (contentData.selectedVersion) {
        html += `data-target-selectedVersion="${contentData.selectedVersion}" `;
    }
    html += `></audio>`;
    html = addDivWrapper(contentData, html);
    return html;
}


export default function AddContentPlugin({ editor, allowedDisplayTypes }) {
    const [showContentDialog, setShowContentDialog] = useState(false);
    const [contentType, setContentType] = useState(null);
    const [loadedContent, setLoadedContent] = useState(null);
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        const getSelectionParent = (selector) => {
            return editor.dom.getParent(editor.selection.getNode(), selector);
        };

        const normalizeZoomValue = (zoom) => {
            if (!zoom) return 'page-fit';

            const value = String(zoom).toLowerCase().trim();
            if (value === 'fith' || value === 'fitv') return 'page-fit';

            const legacyNumericValues = ['25', '50', '75', '150', '200'];
            if (legacyNumericValues.includes(value)) return '100';

            return value;
        };        

        const normalizeBoolean = (value) => {
            if (value === true || value === 'true') return true;
            if (value === false || value === 'false') return false;
            return false;
        };

        const showContentDialog = (e) => {
            setContentType(e.contentType);
            let content = null;
            const element =
                e.contentType === 'video'
                    ? getSelectionParent('div')
                    : e.contentType === 'image'
                    ? editor.selection.getNode()
                    : e.contentType === 'pdf'
                    ? getSelectionParent('div') //editor.dom.createFragment(editor.selection.getContent()).firstElementChild
                    : e.contentType === 'audio'
                    ? getSelectionParent('div')
                    : e.contentType === 'learning'
                    ? (getSelectionParent('div') || getSelectionParent('button') || getSelectionParent('a[href]'))
                    : getSelectionParent('a[href]');
            if (element?.getAttribute('data-target-content')) {
                const widthStr = element.style.width?.trim() || '';      // "100%" or "888px" or ""
                const heightStr = element.style.height?.trim() || '';
                const maxWidthStr = element.style.maxWidth?.trim() || '';
                const aspectRatio = element.style.aspectRatio || '';
                const [aspect_ratio_widthStr = '', aspect_ratio_heightStr = ''] = aspectRatio.split('/').map(v => v.trim());
                const width = widthStr.endsWith('px') ? parseFloat(widthStr) : null;
                const height = heightStr.endsWith('px') ? parseFloat(heightStr) : null;
                const max_width = maxWidthStr.endsWith('px') ? parseFloat(maxWidthStr) : null;
                const aspect_ratio_width = aspect_ratio_widthStr ? parseFloat(aspect_ratio_widthStr) : null;
                const aspect_ratio_height = aspect_ratio_heightStr ? parseFloat(aspect_ratio_heightStr) : null;

                switch (e.contentType) {
                    case 'video':
                        content = {
                            targetEl: element,
                            href: element.getAttribute('href'),
                            auto_play: element.getAttribute('data-target-auto_play') === 'true' ? true : false,
                            poster_image_name: element.getAttribute('data-target-poster_image_name'),
                            poster_image_url: element.getAttribute('data-target-poster_image_url'),
                            poster_id: element.getAttribute('data-target-poster_id'),
                            selectedChapter: Number(element.getAttribute('data-target-selectedChapter')) || null,
                            selectedVersion: Number(element.getAttribute('data-target-selectedVersion')) || null,
                            posterVersion: Number(element.getAttribute('data-target-posterVersion')) || null,
                            show_chapters: element.getAttribute('data-target-show_chapters') === 'true',
                            collapse_chapters: element.getAttribute('data-target-collapse_chapters') === 'true',
                            chapter_buttons_location:
                                element.getAttribute('data-target-chapter_buttons_location') ||
                                ChapterButtonsLocation[1],
                            content_id: Number(element.getAttribute('data-target-contentid')),
                            width: Number(element.children?.[0]?.children?.[0]?.getAttribute('width')),
                            height: Number(element.children?.[0]?.children?.[0]?.getAttribute('height')),
                            max_width: max_width,
                            actionType: 'edit',
                        };
                        break;
                    case 'image':

                        content = {
                            targetEl: element,
                            href: element.getAttribute('src'),
                            selectedVersion: Number(element.getAttribute('data-target-selectedVersion')) || null,
                            content_id: Number(element.getAttribute('data-target-contentid')),
                            width: width,
                            height: height,
                            max_width: max_width,
                            altText: element.getAttribute('alt'),
                            actionType: 'edit',
                        };
                        break;
                    case 'pdf': {
                        content = {
                            targetEl: element,
                            href: element.getAttribute('data-src'),
                            selectedVersion: Number(element.dataset.targetSelectedversion) || null,
                            content_id: Number(element.dataset.targetContentid),
                            //width: width,
                            //height: height,
                            width: aspect_ratio_width,
                            height: aspect_ratio_height,
                            aspect_ratio_width: aspect_ratio_width,                            
                            aspect_ratio_height: aspect_ratio_height,
                            max_width: max_width,
                            title: element.getAttribute('title'),
                            pageNumber: element.getAttribute('data-page'),
                            hideToolbar: element.getAttribute('data-toolbar') === '0',
                            zoom: normalizeZoomValue(element.getAttribute('data-zoom')),
                            pagemode: element.getAttribute('data-pagemode'),
                            fullscreenmode: normalizeBoolean(element.getAttribute('data-fullscreenmode')),
                            btn_fullscreen_opacity: element.getAttribute('data-btn_fullscreen_opacity'),
                            btn_fullscreen_size: element.getAttribute('data-btn_fullscreen_size'),
                           actionType: 'edit',
                        };
                        break;
                    }
                    case 'audio':
                        content = {
                            targetEl: element,
                            href: element.firstElementChild.firstElementChild.getAttribute('src'),
                            selectedVersion: Number(element.getAttribute('data-target-selectedVersion')) || null,
                            content_id: Number(element.getAttribute('data-target-contentid')),
                            altText: element.getAttribute('alt'),
                            actionType: 'edit',
                        };
                        break;
                    case 'learning': {
                        const anchorEl = element.tagName === 'A'
                            ? element
                            : element.querySelector('a');
                        const widthAttr   = element.getAttribute('data-target-width');
                        const heightAttr  = element.getAttribute('data-target-height');
                        const maxWidthStr = element.style.maxWidth?.trim() || '';
                        const max_width   = maxWidthStr.endsWith('px') ? parseFloat(maxWidthStr) : null;

                        content = {
                            targetEl:        element,
                            content_id:      Number(
                                                 element.getAttribute('data-target-contentid')
                                                 || anchorEl?.getAttribute('data-target-contentid')
                                             ),
                            selectedVersion: Number(
                                                 element.getAttribute('data-target-selectedVersion')
                                                 || anchorEl?.getAttribute('data-target-selectedVersion')
                                             ) || null,
                            width:           widthAttr  ? parseFloat(widthAttr)  : null,
                            height:          heightAttr ? parseFloat(heightAttr) : null,
                            max_width:       max_width,
                            actionType:      'edit',
                        };
                        break;
                    }
                    default: {
                        const button = getSelectionParent('button');
                        const target = element.getAttribute('target');
                        content = {
                            targetEl: button || element,
                            href: element.getAttribute('href'),
                            display_mode: target === '_blank' ? 'external' : target === '_self' ? 'inline' : target,
                            reference_mode: element.getAttribute('data-target-refmode'),
                            content_id: Number(element.getAttribute('data-target-contentid')),
                            chapter_id: Number(element.getAttribute('data-chapter-id')),
                            selectedVersion: Number(element?.getAttribute('data-target-selectedVersion')) || null,
                            selectedChapter: Number(element.getAttribute('data-target-selectedChapter')) || null,
                            show_chapters: element.getAttribute('data-target-show_chapters') === 'true',
                            collapse_chapters: element.getAttribute('data-target-collapse_chapters') === 'true',
                            chapter_buttons_location:
                                element.getAttribute('data-target-chapter_buttons_location') ||
                                ChapterButtonsLocation[1],
                            auto_play: element.getAttribute('data-target-auto-play') === 'true',
                            link_type_id: Number(element.getAttribute('data-target-link-type-id')),
                            is_button: button !== null,
                            link_text: element.innerText,
                            actionType: 'edit',
                        };
                        break;
                    }
                }
            }
            setLoadedContent(content);
            setShowContentDialog(true);
        };

        editor.on('addContent', showContentDialog);
        return () => editor.off('addContent', showContentDialog);
    }, [editor]);

    const updateElement = (nodeLinkElm, newContent) => {
        editor.selection.select(nodeLinkElm);
        editor.selection.setContent(newContent);
    };

    const handleSelectedContent = async (contentData) => {
        setShowContentDialog(false);
        if (!contentData) return;

        dispatch(activateLoading());
        let contentName = null;
        let content = null;
        if (contentData.content_id) {
            contentName = await dispatch(fetchContentById({ contentId: contentData.content_id }));
            if (contentData?.selectedVersion) {
                const versions = await dispatch(fetchContentVersions(contentData?.content_id));
                if (versions && versions?.payload.length > 0) {
                    content = versions?.payload?.find(
                        (version) => version.content_version_id === contentData?.selectedVersion
                    );
                }
            }
            if (content == null) {
                content = contentName?.payload?.last_content_version;
            }
        }
        if (content) {
            if (contentType === 'learning' && isUrlLearningContent(content, contentName?.payload?.default_link_type_id)) {
                const url = content.url ?? '';
                if (/^http:\/\//i.test(url)) {
                    enqueueSnackbar(
                        'This Learning Item URL uses http:// which will be blocked by the browser. ' +
                        'Update the URL to https:// in Content Management.',
                        { variant: 'error', action: (key) => <SnackbarDismiss key={key} /> }
                    );
                    dispatch(deactivateLoading());
                    return;
                }
            }

            const html =
                contentType === 'link'
                    ? getLinkHtml(contentData, content, contentName)
                    : contentType === 'video'
                    ? getVideoHtml(contentData, content)
                    : contentType === 'pdf'
                    ? getPdfHtml(contentData, content, selectActiveTheme) // HenryWong
                    : contentType === 'audio'
                    ? getAudioHtml(contentData, content)
                    : contentType === 'learning'
                    ? (isUrlLearningContent(content, contentName?.payload?.default_link_type_id)
                        ? getLearningItemHtmlForUrl(contentData, content)
                        : getLearningItemHtml(contentData, content))
                    : getImageHtml(contentData, content);

            if (loadedContent?.actionType === 'edit') {
                updateElement(loadedContent.targetEl, html);
            } else {
                editor.isInsertingContent = true;
                editor.insertContent(html + '&nbsp;&nbsp;');
            }
        }
        dispatch(deactivateLoading());
    };

    return (
        <>
            {showContentDialog && (
                <SelectContentForm
                    content={loadedContent}
                    onSelectedContent={handleSelectedContent}
                    contentType={contentType}
                    allowedDisplayTypes={allowedDisplayTypes}
                />
            )}
        </>
    );
}

